import React from 'react';
import { ScoreRing, scoreKindLabel, ChangeList, FixNext, InstrumentTag } from '../ds';

// A made-up second version of a full mix, shown on the home and Sonic Insight pages with the same
// components as a real analysis

const EXAMPLE = {
  project: 'Late Night Drive',
  file: 'verse_mix2.wav',
  version: 2,
  score: 7,
  previous: 5,
  kind: 'mastering_readiness',
  headline: 'Almost ready for mastering',
  summary: 'The low end is much cleaner than V1 and the mix feels wider. One thing is still in the way.',
  changes: [
    { label: 'Low end', verdict: 'much tighter', needsAttention: false, explanation: 'The kick and bass no longer blur together.' },
    { label: 'Upper mids', verdict: 'a bit harsh', needsAttention: true, explanation: 'Your vocal and snare are fighting around 3 kHz.' },
  ],
  everythingElse: 'Level and stereo look fine.',
  fix: {
    title: 'Give the vocal its own space',
    why: 'The snare is sitting right on top of the vocal, so the words get lost in the chorus.',
    moves: [
      { plugin: 'EQ Eight', target: 'on the snare', purpose: 'make room for the vocal', settings: [['Frequency', '3 kHz'], ['Gain', '−2.5 dB'], ['Q', '1.4']] },
      { plugin: 'Compressor', target: 'on the vocal', purpose: 'keep it steady', settings: [['Ratio', '3:1'], ['Attack', '10 ms'], ['Release', '80 ms']] },
    ],
  },
};

// Decorative waveform: amber, with the 3 kHz clash marked in coral
const BARS = [18, 26, 34, 22, 40, 58, 46, 70, 62, 84, 76, 92, 68, 54, 80, 96, 88, 72, 60, 78, 90, 100, 86, 64, 52, 70, 82, 94, 76, 58, 46, 62, 74, 66, 50, 38, 56, 68, 48, 36];

/** The compact version for the home page hero */
export const ExampleSummary = () => (
  <div className="flex flex-col gap-space-6 p-space-6 sm:p-space-7 rounded-radius-xl bg-surface font-text">
    <div className="flex flex-wrap items-center justify-between gap-space-3">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-caption text-muted">{EXAMPLE.project}</span>
        <span className="font-mono text-data-sm text-ink truncate">{EXAMPLE.file} · V{EXAMPLE.version}</span>
      </div>
      <InstrumentTag category="Full mix" on="surface" />
    </div>
    <div aria-hidden="true" className="h-24 flex items-center gap-1">
      {BARS.map((height, i) => (
        <span key={i} className={`flex-1 rounded-sm ${i >= 26 && i <= 31 ? 'bg-coral' : 'bg-amber'}`} style={{ height: `${height}%` }} />
      ))}
    </div>
    <div className="flex flex-wrap items-center gap-space-5">
      <ScoreRing score={EXAMPLE.score} kind={EXAMPLE.kind} previous={EXAMPLE.previous} previousVersion={1} />
      <div className="flex-[1_1_200px] min-w-0 flex flex-col gap-space-2">
        <span className="text-label text-muted">{scoreKindLabel(EXAMPLE.kind)}</span>
        <span className="font-display text-title-3 text-ink">{EXAMPLE.headline}</span>
        <span className="text-small text-ink-soft">{EXAMPLE.summary}</span>
      </div>
    </div>
  </div>
);

/** The whole analysis card, as the clip chat shows it */
export const ExampleCard = () => (
  <article aria-label="Example analysis" className="flex flex-col gap-space-7 p-space-6 sm:p-space-8 rounded-radius-xl bg-surface font-text">
    <div className="flex flex-wrap items-center gap-space-6">
      <ScoreRing score={EXAMPLE.score} kind={EXAMPLE.kind} previous={EXAMPLE.previous} previousVersion={1} />
      <div className="flex-[1_1_260px] min-w-0 flex flex-col gap-space-2">
        <span className="text-label text-muted">{scoreKindLabel(EXAMPLE.kind)}</span>
        <h3 className="m-0 font-display text-title-1 text-ink">{EXAMPLE.headline}</h3>
        <p className="m-0 text-lead text-ink-soft">{EXAMPLE.summary}</p>
      </div>
    </div>
    <ChangeList previousVersion={1} changes={EXAMPLE.changes} everythingElse={EXAMPLE.everythingElse} />
    <FixNext title={EXAMPLE.fix.title} why={EXAMPLE.fix.why} moves={EXAMPLE.fix.moves} />
  </article>
);
